import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Tabs,
  Tab,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Button
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import MainLayout from '../layouts/MainLayout';
import { clienteService, Cliente, CreateClienteDTO } from '../services/clienteService';
import { apoliceService, Apolice } from '../services/apoliceService';
import {  formatarMoeda } from '../utils/formatters';
import ApoliceFormModal from '../components/ApoliceFormModal';
import { useAuthContext } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const DetalharClientePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuthContext();

  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [apolices, setApolices] = useState<Apolice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [modalApoliceOpen, setModalApoliceOpen] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('ID do cliente não fornecido.');
      setLoading(false);
      return;
    }

    const carregarDados = async () => {
      try {
        setLoading(true);
        const [clienteData, apolicesData] = await Promise.all([
          clienteService.detalhar(Number(id)),
          apoliceService.listarPorCliente(Number(id))
        ]);
        setCliente(clienteData);
        setApolices(apolicesData);
      } catch (err) {
        setError('Erro ao carregar os dados do cliente.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativa': return 'success';
      case 'Cancelada': return 'error';
      case 'Vencida': return 'warning';
      case 'Renovação': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => navigate('/clientes')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Detalhes do Cliente
          </Typography>
        </Box>

        <Paper>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="abas de detalhes do cliente">
              <Tab label="Dados do Cliente" />
              <Tab label={`Apólices (${apolices.length})`} />
            </Tabs>
          </Box>

          {/* Aba de Dados do Cliente */}
          <TabPanel value={tabValue} index={0}>
            {cliente && (
              <Box>
                <Typography variant="h6" gutterBottom>Dados Pessoais</Typography>
                <Grid container spacing={2}>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Nome Completo</Typography>
                    <Typography variant="body1">{cliente.nome || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">CPF/CNPJ</Typography>
                    <Typography variant="body1">{cliente.cpfCnpj || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">RG</Typography>
                    <Typography variant="body1">{cliente.rg || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Data de Nascimento</Typography>
                    <Typography variant="body1">{cliente.dataNascimento ? formatarData(cliente.dataNascimento) : '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Profissão</Typography>
                    <Typography variant="body1">{cliente.profissao || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Estado Civil</Typography>
                    <Typography variant="body1">{cliente.estadoCivil || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Sexo</Typography>
                    <Typography variant="body1">{cliente.sexo || '-'}</Typography>
                  </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Contato</Typography>
                <Grid container spacing={2}>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{cliente.email || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Telefone Celular</Typography>
                    <Typography variant="body1">{cliente.telefoneCelular || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Telefone Comercial</Typography>
                    <Typography variant="body1">{cliente.telefoneComercial || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Telefone Residencial</Typography>
                    <Typography variant="body1">{cliente.telefoneResidencial || '-'}</Typography>
                  </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Endereço</Typography>
                <Grid container spacing={2}>
                  <Grid size={{xs: 12,sm:3}}>
                    <Typography variant="subtitle2" color="text.secondary">CEP</Typography>
                    <Typography variant="body1">{cliente.cep || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:9}}>
                    <Typography variant="subtitle2" color="text.secondary">Logradouro</Typography>
                    <Typography variant="body1">{cliente.endereco || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:6}}>
                    <Typography variant="subtitle2" color="text.secondary">Bairro</Typography>
                    <Typography variant="body1">{cliente.bairro || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:4}}>
                    <Typography variant="subtitle2" color="text.secondary">Cidade</Typography>
                    <Typography variant="body1">{cliente.cidade || '-'}</Typography>
                  </Grid>
                  <Grid size={{xs: 12,sm:2}}>
                    <Typography variant="subtitle2" color="text.secondary">UF</Typography>
                    <Typography variant="body1">{cliente.uf || '-'}</Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </TabPanel>

          {/* Aba de Apólices */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              {hasPermission({ entidade: 'apolice', acao: 'can_create' }) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setModalApoliceOpen(true)}
                >
                  Nova Apólice
                </Button>
              )}
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Número</TableCell>
                    <TableCell>Ramo</TableCell>
                    <TableCell>Seguradora</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Vigência</TableCell>
                    <TableCell align="right">Prêmio Total</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apolices.length > 0 ? (
                    apolices.map((apolice) => (
                      <TableRow key={apolice.id} hover>
                        <TableCell>{apolice.numero}</TableCell>
                        <TableCell>{apolice.ramo?.nome}</TableCell>
                        <TableCell>{apolice.seguradora.nome}</TableCell>
                        <TableCell>
                          <Chip label={apolice.status} color={getStatusColor(apolice.status)} size="small" />
                        </TableCell>
                        <TableCell>{`${formatarData(apolice.dataInicio)} a ${formatarData(apolice.dataFim)}`}</TableCell>
                        <TableCell align="right">{formatarMoeda(apolice.premioTotal || 0)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar Apólice">
                            <IconButton size="small" onClick={() => navigate(`/apolices/editar/${apolice.id}`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Nenhuma apólice encontrada para este cliente.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Paper>

        <ApoliceFormModal
          open={modalApoliceOpen}
          onClose={() => setModalApoliceOpen(false)}
          onSave={() => { setModalApoliceOpen(false) }}
          clientePreSelecionado={cliente}
        />
      </Container>
    </MainLayout>
  );
};

export default DetalharClientePage;